import { useMemo } from 'react'
import { StockBySkuResponse } from '../types'
import { Batch } from '../../../interfaces/Batch'

interface UseBatchSelectionProps {
  stockItems: StockBySkuResponse[]
  hasBatchManagement?: boolean
  hasExpirationDate?: boolean
}

interface UseBatchSelectionReturn {
  shouldShowBatchModal: boolean
  preparedBatches: Batch[]
  totalStock: number
}

/**
 * Custom hook to handle FEFO (First-Expired-First-Out) batch selection logic
 * Determines if batch modal should be shown and prepares batch data
 */
export const useBatchSelection = ({
  stockItems,
  hasBatchManagement = false,
  hasExpirationDate = false
}: UseBatchSelectionProps): UseBatchSelectionReturn => {

  const shouldShowBatchModal = useMemo(() => {
    // Si el SKU no tiene manejo de lotes NI fechas → no mostrar modal
    if (!hasBatchManagement && !hasExpirationDate) {
      return false
    }

    // Si SKU tiene configuración → verificar si stock tiene datos válidos
    const hasStockBatchData = stockItems.some(item =>
      item.batch != null && (
        (item.batch.batchNumber && item.batch.batchNumber.trim() !== '') ||
        item.batch.expirationDate
      )
    )

    return hasStockBatchData
  }, [stockItems, hasBatchManagement, hasExpirationDate])

  const preparedBatches = useMemo(() => {
    if (!shouldShowBatchModal) {
      return []
    }

    // Convertir los datos del backend al formato esperado por el modal
    const batches: Batch[] = stockItems
      .filter(item => item.batch != null)
      .map((item) => ({
        batchId: item.batch!.batchId,
        batchNumber: item.batch!.batchNumber || '',
        expirationDate: item.batch!.expirationDate || '',
        quantity: item.quantity
      }))

    return batches
  }, [stockItems, shouldShowBatchModal])

  const totalStock = useMemo(() => {
    return stockItems.reduce((total, item) => total + (item.quantity || 0), 0)
  }, [stockItems])

  return {
    shouldShowBatchModal,
    preparedBatches,
    totalStock
  }
}